import { Injectable } from "@angular/core";
import { SearchDisplayMode, SearchTypeMode, SearchInputType } from "../../../model";
import { SearchInputValidator, ValidationResult } from "./search-input-validator.service";

/**
 * Unified service for coordinating search mode logic and validation
 * Updated to use the new SearchInputType enum and unified validation
 */
@Injectable({
  providedIn: "root",
})
export class SearchModeCoordinator {
  constructor(private inputValidator: SearchInputValidator) {}

  /**
   * Determines if search execution is allowed for given input and display mode
   * @param inputType The type of input detected
   * @param displayMode Current display mode
   * @returns ValidationResult with execution permission
   */
  canExecute(inputType: SearchInputType, displayMode: SearchDisplayMode): ValidationResult {
    return this.inputValidator.validateInputForExecution(inputType, displayMode);
  }

  /**
   * Determines if suggestion type can be shown in autocomplete for current display mode
   * @param inputType The type of input being evaluated
   * @param displayMode Current display mode
   * @returns ValidationResult with suggestion permission
   */
  canSuggest(inputType: SearchInputType, displayMode: SearchDisplayMode): ValidationResult {
    return this.inputValidator.validateSuggestionForAutocomplete(inputType, displayMode);
  }

  /**
   * Determines if Google Places API should be called for current display mode
   * @param displayMode Current display mode
   * @returns ValidationResult with Places API permission
   */
  canCallPlacesApi(displayMode: SearchDisplayMode): ValidationResult {
    return this.inputValidator.validatePlacesApiCall(displayMode);
  }

  /**
   * Gets human-readable reason why an action was blocked
   * @param inputType The input type that was blocked
   * @param displayMode Current display mode
   * @param actionType Type of action that was blocked
   * @returns User-friendly explanation
   */
  getBlockedReason(inputType: SearchInputType, displayMode: SearchDisplayMode, actionType: string): string {
    return this.inputValidator.getBlockedReason(inputType, displayMode, actionType as any);
  }

  /**
   * Determines which search type modes should be enabled for current display mode
   * @param displayMode Current display mode
   * @returns Array of enabled search type modes
   */
  getEnabledSearchTypeModes(displayMode: SearchDisplayMode): SearchTypeMode[] {
    // AI and Property modes: disable all search type modes
    if (displayMode === SearchDisplayMode.AI || displayMode === SearchDisplayMode.PROPERTY) {
      return [];
    }

    // Map mode: enable all search type modes
    if (displayMode === SearchDisplayMode.MAP) {
      return [SearchTypeMode.TEXT_SEARCH, SearchTypeMode.LOCATION_SEARCH, SearchTypeMode.IMAGE_SEARCH];
    }

    // Grid mode: only enable text search and image search (image search available but without map tab)
    if (displayMode === SearchDisplayMode.GRID) {
      return [SearchTypeMode.TEXT_SEARCH, SearchTypeMode.IMAGE_SEARCH];
    }

    return [];
  }

  /**
   * Validates if a search type mode is allowed for current display mode
   * @param searchTypeMode The search type mode to validate
   * @param displayMode Current display mode
   * @returns ValidationResult with permission
   */
  validateSearchTypeMode(searchTypeMode: SearchTypeMode, displayMode: SearchDisplayMode): ValidationResult {
    const enabledModes = this.getEnabledSearchTypeModes(displayMode);

    if (enabledModes.includes(searchTypeMode)) {
      return { valid: true };
    }

    let reason = "";
    if (displayMode === SearchDisplayMode.AI || displayMode === SearchDisplayMode.PROPERTY) {
      reason = `Search is not available in ${displayMode.toUpperCase()} mode.`;
    } else if (displayMode === SearchDisplayMode.GRID) {
      if (searchTypeMode === SearchTypeMode.LOCATION_SEARCH) {
        reason = "Location search is only available in Map mode. Switch to Map view to search by location.";
      }
      // Image search is allowed in Grid mode but without map selection tab
    }

    return {
      valid: false,
      reasons: [reason],
      blockedType: "execution" as any,
    };
  }

  /**
   * Determines if search form should be enabled for current display mode
   * @param displayMode Current display mode
   * @returns boolean indicating if search should be enabled
   */
  isSearchEnabled(displayMode: SearchDisplayMode): boolean {
    return displayMode === SearchDisplayMode.GRID || displayMode === SearchDisplayMode.MAP;
  }

  /**
   * Filters historical suggestions based on current display mode compatibility
   * @param suggestions Array of historical suggestions with their types
   * @param displayMode Current display mode
   * @returns Filtered array of compatible suggestions
   */
  filterHistoricalSuggestions(
    suggestions: Array<{ type: SearchInputType; text: string }>,
    displayMode: SearchDisplayMode,
  ): Array<{ type: SearchInputType; text: string }> {
    return suggestions.filter((suggestion) => {
      const validation = this.canSuggest(suggestion.type, displayMode);
      return validation.valid;
    });
  }

  /**
   * Gets display mode specific configuration for search behavior
   * @param displayMode Current display mode
   * @returns Configuration object with display mode specific settings
   */
  getDisplayModeConfig(displayMode: SearchDisplayMode) {
    return {
      searchEnabled: this.isSearchEnabled(displayMode),
      enabledSearchTypeModes: this.getEnabledSearchTypeModes(displayMode),
      allowPlacesApi: this.canCallPlacesApi(displayMode).valid,
      allowedInputTypes: this.inputValidator.getAllowedInputTypes(displayMode),
    };
  }
}
